import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bookmark,
  BookmarkCheck,
  ChevronRight,
  FileText,
  Pencil,
  Plus,
  Scale,
  Search,
  Send,
  Sparkles,
  Star,
  X,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import { useChat, type ChatSettings } from '../hooks/useChat'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '社区法律基金会',
    searchPlaceholder: '搜索马来西亚法律知识...',
    topicsTitle: '法律知识主题',
    topicsCount: (n: number) => `共 ${n} 个主题`,
    articles: '篇法律文章',
    hotArticles: '热门文章',
    aiAssistant: 'AI 法律助手',
    aiDesc: '基于社区法律基金会的马来西亚法律知识库，为您解答法律问题并提供分析建议',
    aiSuggestions: ['查询离婚程序要求', '了解合同解除条款', '确认诉讼时效期限'],
    aiInputPlaceholder: '输入法律问题...',
    myBookmarks: '我的收藏',
    noBookmarks: '暂无收藏内容',
    noBookmarksDesc: '浏览时收藏常用法律知识',
    unbookmark: '取消收藏',
    viewTopic: '查看主题',
    aboutTitle: '关于社区法律基金会',
    aboutItems: [
      { title: '法律法规知识', desc: '马来西亚主要法律法规、法典及修订版本的完整收录' },
      { title: '附属法规与命令', desc: 'P.U.(A)、P.U.(B)、生效令、收费条例等附属立法文件' },
      { title: '文书规范', desc: '马来西亚法庭文书格式要求及常用表达习惯' },
    ],
    new: '新增',
    createTopicTitle: '新增法律主题',
    createTopicDesc: '主题会立即出现在社区法律基金会，关键词用于自动匹配法规文件。',
    topicName: '主题名称',
    topicDesc: '主题描述',
    keywords: '匹配关键词（英文逗号分隔）',
    placeholderName: '例如：公司法',
    placeholderDesc: '该主题覆盖的法规范围',
    placeholderKeywords: 'company, companies, corporate, directors',
    cancel: '取消',
    create: '创建主题',
    creating: '创建中...',
  },
  en: {
    pageTitle: 'Community Law Foundation',
    searchPlaceholder: 'Search Malaysian legal knowledge...',
    topicsTitle: 'Legal Knowledge Topics',
    topicsCount: (n: number) => `${n} topics`,
    articles: 'legal articles',
    hotArticles: 'Popular Articles',
    aiAssistant: 'AI Legal Assistant',
    aiDesc: 'Based on Community Law Foundation\'s Malaysian legal knowledge base, providing legal analysis and recommendations',
    aiSuggestions: ['Query divorce procedures', 'Contract termination clauses', 'Limitation period'],
    aiInputPlaceholder: 'Ask a legal question...',
    myBookmarks: 'My Bookmarks',
    noBookmarks: 'No bookmarks yet',
    noBookmarksDesc: 'Bookmark useful legal knowledge while browsing',
    unbookmark: 'Remove',
    viewTopic: 'View topic',
    aboutTitle: 'About Community Law Foundation',
    aboutItems: [
      { title: 'Legal Statutes', desc: 'Complete collection of Malaysian laws, codes and amendments' },
      { title: 'Subsidiary Legislation', desc: 'P.U.(A), P.U.(B), commencement orders, fee regulations and related instruments' },
      { title: 'Document Standards', desc: 'Malaysian court document formats and conventions' },
    ],
    new: 'New',
    createTopicTitle: 'Create Legal Topic',
    createTopicDesc: 'The topic appears immediately in Community Law Foundation; keywords are used to match statute files.',
    topicName: 'Topic Name',
    topicDesc: 'Topic Description',
    keywords: 'Keywords (comma-separated)',
    placeholderName: 'e.g., Company Law',
    placeholderDesc: 'Scope of this topic',
    placeholderKeywords: 'company, companies, corporate, directors',
    cancel: 'Cancel',
    create: 'Create Topic',
    creating: 'Creating...',
  },
}

interface LegalTopic {
  topic_key: string
  name_zh: string
  name_en: string
  desc_zh: string
  desc_en: string
  keywords: string[]
  sort_order: number
  enabled: boolean
  file_count: number
  last_updated?: string
}

interface RecentFile {
  precedent_id: string
  case_name: string
  citation?: string
  court?: string
  decision_date?: string
  categories: string[]
  created_at?: string
}

// ─── localStorage 收藏管理 ─────────────────────────────────────────────────
const BOOKMARKS_KEY = 'legal_base_bookmarks'

function getBookmarks(): string[] {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveBookmarks(keys: string[]) {
  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(keys))
}

export function LegalBase() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const navigate = useNavigate()

  const [topics, setTopics] = useState<LegalTopic[]>([])
  const [showCreateTopic, setShowCreateTopic] = useState(false)
  const [creatingTopic, setCreatingTopic] = useState(false)
  const [topicForm, setTopicForm] = useState({
    name: '',
    desc: '',
    keywords: '',
  })
  const [editingTopic, setEditingTopic] = useState<LegalTopic | null>(null)
  const [editForm, setEditForm] = useState({
    name_zh: '',
    name_en: '',
    desc_zh: '',
    desc_en: '',
    keywords: '',
  })
  const [savingEdit, setSavingEdit] = useState(false)

  const loadTopics = () => {
    fetch('/knowledge/legal-topics')
      .then((r) => r.json())
      .then((data: { topics?: LegalTopic[] }) => {
        setTopics(data.topics ?? [])
      })
      .catch(() => {})
  }

  useEffect(() => {
    loadTopics()
  }, [])

  const createTopic = () => {
    const name = topicForm.name.trim()
    if (!name || creatingTopic) return
    setCreatingTopic(true)
    fetch('/knowledge/legal-topics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name,
        desc: topicForm.desc.trim(),
        keywords: topicForm.keywords.split(',').map((kw) => kw.trim()).filter(Boolean),
        sort_order: topics.length * 10 + 100,
        enabled: true,
        lang: lang, // 传递当前语言
      }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(() => {
        setTopicForm({ name: '', desc: '', keywords: '' })
        setShowCreateTopic(false)
        loadTopics()
      })
      .catch(() => {})
      .finally(() => setCreatingTopic(false))
  }

  const openEditTopic = (topic: LegalTopic) => {
    setEditingTopic(topic)
    setEditForm({
      name_zh: topic.name_zh,
      name_en: topic.name_en,
      desc_zh: topic.desc_zh,
      desc_en: topic.desc_en,
      keywords: topic.keywords.join(', '),
    })
  }

  const saveEditTopic = () => {
    if (!editingTopic || savingEdit) return
    setSavingEdit(true)
    const payload: Record<string, any> = {}
    if (editForm.name_zh !== editingTopic.name_zh) payload.name_zh = editForm.name_zh.trim()
    if (editForm.name_en !== editingTopic.name_en) payload.name_en = editForm.name_en.trim()
    if (editForm.desc_zh !== editingTopic.desc_zh) payload.desc_zh = editForm.desc_zh.trim()
    if (editForm.desc_en !== editingTopic.desc_en) payload.desc_en = editForm.desc_en.trim()
    const newKw = editForm.keywords.split(',').map((kw) => kw.trim()).filter(Boolean)
    if (newKw.join(',') !== editingTopic.keywords.join(',')) payload.keywords = newKw

    fetch(`/knowledge/legal-topics/${editingTopic.topic_key}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(() => {
        setEditingTopic(null)
        loadTopics()
      })
      .catch(() => {})
      .finally(() => setSavingEdit(false))
  }

  // ─── 热门文章（从 API 加载）─────────────────────────────────────────────────
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([])
  const [viewingFile, setViewingFile] = useState<{ meta: any; content: string } | null>(null)
  const [loadingContent, setLoadingContent] = useState(false)

  useEffect(() => {
    fetch('/knowledge/categories/statutes/files?limit=6&shared=true')
      .then((r) => r.json())
      .then((data) => {
        if (data.files) setRecentFiles(data.files)
      })
      .catch(() => {})
  }, [])

  const openFile = (precedentId: string) => {
    setLoadingContent(true)
    fetch(`/knowledge/files/${precedentId}/content?shared=true`)
      .then((r) => r.json())
      .then((data) => {
        setViewingFile(data)
      })
      .catch(() => {})
      .finally(() => setLoadingContent(false))
  }

  // ─── 收藏状态 ─────────────────────────────────────────────────────────────
  const [bookmarks, setBookmarks] = useState<string[]>(getBookmarks)

  const toggleBookmark = (key: string) => {
    setBookmarks((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
      saveBookmarks(next)
      return next
    })
  }

  // ─── AI 助手 ──────────────────────────────────────────────────────────────
  const chatSettings: ChatSettings = {
    mode: 'hybrid',
    top_k: 8,
    phrase: false,
    temperature: 0.1,
    categoryPresets: [],
    rawCategories: ['法令', 'subsidiary_legislation', 'amendment_act', 'regulations', 'schedule_amendment', 'fees', 'commencement_order'],
  }

  const { history, pending, error, isStreaming, send, stop } = useChat()
  const [aiInput, setAiInput] = useState('')
  const aiResponseRef = useRef<HTMLDivElement>(null)

  const handleAiSend = (question: string) => {
    const q = question.trim()
    if (!q || isStreaming) return
    setAiInput('')
    send(q, chatSettings)
  }

  // Auto-scroll AI response
  useEffect(() => {
    if (aiResponseRef.current) {
      aiResponseRef.current.scrollTop = aiResponseRef.current.scrollHeight
    }
  }, [pending?.partial, history.length])

  // 最新一条对话回复
  const latestAnswer = pending
    ? pending.partial
    : history.length > 0
      ? history[history.length - 1]?.content ?? ''
      : ''
  const showAiResponse = latestAnswer.length > 0 || isStreaming

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 overflow-y-auto">
        {/* 搜索栏 */}
        <div className="px-6 pt-6 pb-4">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-400" />
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              className="w-full pl-12 pr-12 py-3 rounded-xl border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 bg-white"
            />
            <kbd className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] bg-brand-100 px-2 py-0.5 rounded text-brand-500">
              ⌘K
            </kbd>
          </div>
        </div>

        <div className="px-6 pb-6">
          <div className="flex gap-6">
            {/* 左侧主内容 */}
            <div className="flex-1 min-w-0">
              {/* 法律知识主题 */}
              <div className="bg-white rounded-xl border border-brand-200 p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-brand-800">
                      {t.topicsTitle}
                    </h3>
                    <div className="text-sm text-brand-400 mt-0.5">
                      {t.topicsCount(topics.length)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCreateTopic(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {lang === 'zh' ? '新增主题' : 'New topic'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {topics.map((topic) => {
                    const name = lang === 'zh' ? topic.name_zh : topic.name_en
                    const desc = lang === 'zh' ? topic.desc_zh : topic.desc_en
                    const isBookmarked = bookmarks.includes(topic.topic_key)
                    return (
                      <div
                        key={topic.topic_key}
                        className="relative flex items-center gap-3 p-4 rounded-lg border border-brand-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer group"
                      >
                        <div
                          className="flex-1 flex items-center gap-3 min-w-0"
                          onClick={() => navigate(`/legal-base/${topic.topic_key}`)}
                        >
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Scale className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-brand-800 truncate">
                              {name}
                            </div>
                            <p className="text-xs text-brand-400 mt-0.5 truncate">
                              {desc || (lang === 'zh' ? '自定义法律法规主题' : 'Custom legal statute topic')}
                            </p>
                            <div className="text-[11px] text-brand-400 mt-1 flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {topic.file_count} {t.articles}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            openEditTopic(topic)
                          }}
                          className="p-1.5 rounded-lg text-brand-300 hover:text-blue-500 hover:bg-blue-50 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                          title={lang === 'zh' ? '编辑' : 'Edit'}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleBookmark(topic.topic_key)
                          }}
                          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                            isBookmarked
                              ? 'text-amber-500 hover:bg-amber-50'
                              : 'text-brand-300 hover:text-amber-500 hover:bg-amber-50 opacity-0 group-hover:opacity-100'
                          }`}
                          title={isBookmarked ? t.unbookmark : '收藏'}
                        >
                          {isBookmarked ? (
                            <BookmarkCheck className="w-4 h-4" />
                          ) : (
                            <Bookmark className="w-4 h-4" />
                          )}
                        </button>
                        <ChevronRight
                          className="w-4 h-4 text-brand-300 group-hover:text-blue-500 flex-shrink-0 transition-colors"
                          onClick={() => navigate(`/legal-base/${topic.topic_key}`)}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* 关于法律知识底座 */}
              <AboutSection
                lang={lang}
                t={t}
                openFile={openFile}
                classifiedCount={topics.reduce((sum, topic) => sum + topic.file_count, 0)}
              />
            </div>

            {/* 右侧边栏 */}
            <div className="w-72 flex-shrink-0 space-y-5">
              {/* 热门文章 */}
              <div className="bg-white rounded-xl border border-brand-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-amber-500" />
                  <h4 className="text-sm font-semibold text-brand-800">
                    {t.hotArticles}
                  </h4>
                </div>
                <div className="space-y-3">
                  {recentFiles.map((file) => {
                    const tag = file.categories?.[0] ?? ''
                    const date = file.decision_date ?? file.created_at?.slice(0, 10) ?? ''
                    return (
                      <div
                        key={file.precedent_id}
                        onClick={() => openFile(file.precedent_id)}
                        className="flex items-start gap-2 cursor-pointer hover:bg-brand-50 -mx-2 px-2 py-1.5 rounded transition-colors"
                      >
                        <FileText className="w-4 h-4 text-brand-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-brand-700 font-medium truncate">
                            {file.case_name}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-brand-400">
                            <span>{tag}</span>
                            {tag && date && <span>·</span>}
                            <span>{date}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {recentFiles.length === 0 && (
                    <div className="text-xs text-brand-400 text-center py-3">
                      {lang === 'zh' ? '暂无文章' : 'No articles yet'}
                    </div>
                  )}
                </div>
              </div>

              {/* AI 法律助手 */}
              <div className="rounded-xl bg-gradient-to-br from-navy-950 to-blue-800 p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4" />
                  <h4 className="text-sm font-semibold">{t.aiAssistant}</h4>
                </div>
                <p className="text-xs text-white/70 mb-3 leading-relaxed">
                  {t.aiDesc}
                </p>

                {/* AI 回复区域 */}
                {showAiResponse && (
                  <div
                    ref={aiResponseRef}
                    className="mb-3 max-h-40 overflow-y-auto rounded-lg bg-white/10 p-3 text-xs leading-relaxed text-white/90 whitespace-pre-wrap"
                  >
                    {latestAnswer || (
                      <span className="text-white/50 animate-pulse">思考中...</span>
                    )}
                  </div>
                )}
                {error && (
                  <div className="mb-2 text-xs text-red-300">{error}</div>
                )}

                {/* 建议问题按钮 */}
                {!showAiResponse && (
                  <div className="space-y-2 mb-3">
                    {t.aiSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleAiSend(suggestion)}
                        className="w-full text-left px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {/* 输入框 */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleAiSend(aiInput)
                      }
                    }}
                    placeholder={t.aiInputPlaceholder}
                    className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-xs text-white placeholder:text-white/50 focus:outline-none focus:bg-white/15 border border-white/20"
                  />
                  {isStreaming ? (
                    <button
                      type="button"
                      onClick={stop}
                      className="p-2 rounded-lg bg-red-500/80 hover:bg-red-500 transition-colors"
                    >
                      <div className="w-3 h-3 rounded-sm bg-white" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAiSend(aiInput)}
                      disabled={!aiInput.trim()}
                      className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-30"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* 我的收藏 */}
              <div className="bg-white rounded-xl border border-brand-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Bookmark className="w-4 h-4 text-brand-500" />
                  <h4 className="text-sm font-semibold text-brand-800">
                    {t.myBookmarks}
                  </h4>
                </div>
                {bookmarks.length === 0 ? (
                  <div className="flex flex-col items-center py-6 text-center">
                    <Bookmark className="w-8 h-8 text-brand-200 mb-2" />
                    <div className="text-sm text-brand-500">{t.noBookmarks}</div>
                    <div className="text-xs text-brand-400 mt-0.5">
                      {t.noBookmarksDesc}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bookmarks.map((key) => {
                      const topic = topics.find((tp) => tp.topic_key === key)
                      if (!topic) return null
                      const name = lang === 'zh' ? topic.name_zh : topic.name_en
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-2 p-2 -mx-1 rounded-lg hover:bg-brand-50 transition-colors group/bm"
                        >
                          <BookmarkCheck className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          <button
                            type="button"
                            onClick={() => navigate(`/legal-base/${key}`)}
                            className="flex-1 text-left text-sm text-brand-700 font-medium truncate hover:text-blue-600"
                          >
                            {name}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleBookmark(key)}
                            className="text-[11px] text-brand-400 hover:text-red-500 opacity-0 group-hover/bm:opacity-100 transition-opacity"
                          >
                            {t.unbookmark}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCreateTopic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateTopic(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-lg font-semibold text-brand-800">
                  {t.createTopicTitle}
                </h3>
                <p className="text-sm text-brand-400 mt-1">
                  {t.createTopicDesc}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateTopic(false)}
                className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-brand-600">{t.topicName}</span>
                <input
                  value={topicForm.name}
                  onChange={(e) => setTopicForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                  placeholder={t.placeholderName}
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-brand-600">{t.topicDesc}</span>
                <input
                  value={topicForm.desc}
                  onChange={(e) => setTopicForm((prev) => ({ ...prev, desc: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                  placeholder={t.placeholderDesc}
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-brand-600">
                  {t.keywords}
                </span>
                <input
                  value={topicForm.keywords}
                  onChange={(e) => setTopicForm((prev) => ({ ...prev, keywords: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                  placeholder={t.placeholderKeywords}
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateTopic(false)}
                className="px-4 py-2 rounded-lg border border-brand-200 text-sm text-brand-600 hover:bg-brand-50"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={createTopic}
                disabled={!topicForm.name.trim() || creatingTopic}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingTopic ? t.creating : t.create}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑主题弹窗 */}
      {editingTopic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditingTopic(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-lg font-semibold text-brand-800">
                  {lang === 'zh' ? '编辑主题' : 'Edit Topic'}
                </h3>
                <p className="text-sm text-brand-400 mt-1">
                  {lang === 'zh' ? '分别填写中英文名称和描述，切换语言时各自显示。' : 'Fill in Chinese and English names separately; each language shows its own text.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingTopic(null)}
                className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-brand-600">{lang === 'zh' ? '中文名称' : 'Chinese Name'}</span>
                  <input
                    value={editForm.name_zh}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name_zh: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="例如：公司法"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-brand-600">{lang === 'zh' ? '英文名称' : 'English Name'}</span>
                  <input
                    value={editForm.name_en}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name_en: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="e.g., Company Law"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-brand-600">{lang === 'zh' ? '中文描述' : 'Chinese Description'}</span>
                  <input
                    value={editForm.desc_zh}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, desc_zh: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="该主题覆盖的法规范围"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-brand-600">{lang === 'zh' ? '英文描述' : 'English Description'}</span>
                  <input
                    value={editForm.desc_en}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, desc_en: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                    placeholder="Scope of this topic"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-brand-600">{t.keywords}</span>
                <input
                  value={editForm.keywords}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, keywords: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400"
                  placeholder={t.placeholderKeywords}
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setEditingTopic(null)}
                className="px-4 py-2 rounded-lg border border-brand-200 text-sm text-brand-600 hover:bg-brand-50"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={saveEditTopic}
                disabled={savingEdit}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {savingEdit ? (lang === 'zh' ? '保存中...' : 'Saving...') : (lang === 'zh' ? '保存' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 文档查看弹窗 */}
      {viewingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewingFile(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between p-6 border-b border-brand-100">
              <div className="flex-1 min-w-0 pr-4">
                <h3 className="text-lg font-semibold text-brand-800 truncate">
                  {viewingFile.meta.case_name}
                </h3>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-brand-500">
                  {viewingFile.meta.citation && (
                    <span className="bg-brand-50 px-2 py-0.5 rounded">{viewingFile.meta.citation}</span>
                  )}
                  {viewingFile.meta.court && (
                    <span className="bg-brand-50 px-2 py-0.5 rounded">{viewingFile.meta.court}</span>
                  )}
                  {viewingFile.meta.decision_date && (
                    <span className="bg-brand-50 px-2 py-0.5 rounded">{viewingFile.meta.decision_date}</span>
                  )}
                  {viewingFile.meta.jurisdiction && (
                    <span className="bg-brand-50 px-2 py-0.5 rounded">{viewingFile.meta.jurisdiction}</span>
                  )}
                </div>
                {viewingFile.meta.catchwords && (
                  <p className="text-xs text-brand-400 mt-2 line-clamp-2">
                    {viewingFile.meta.catchwords}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setViewingFile(null)}
                className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingContent ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="prose prose-sm max-w-none text-brand-700 whitespace-pre-wrap leading-relaxed">
                  {viewingFile.content || (lang === 'zh' ? '暂无正文内容' : 'No content available')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── 关于法律知识底座（动态统计 + 展开预览） ─────────────────────────────────
const ABOUT_SECTIONS = [
  {
    key: 'statutes',
    title_zh: '法律法规知识',
    title_en: 'Legal Statutes',
    desc_zh: '马来西亚主要法律法规、法典及修订版本的完整收录',
    desc_en: 'Complete collection of Malaysian laws, codes and amendments',
    icon: 'file',
  },
  {
    key: 'subsidiary',
    title_zh: '附属法规与命令',
    title_en: 'Subsidiary Legislation',
    desc_zh: 'P.U.(A)、P.U.(B)、生效令、收费条例等附属立法文件',
    desc_en: 'P.U.(A), P.U.(B), commencement orders, fee regulations and related instruments',
    icon: 'file',
  },
  {
    key: 'documents',
    title_zh: '文书规范',
    title_en: 'Document Standards',
    desc_zh: '马来西亚法庭文书格式要求及常用表达习惯',
    desc_en: 'Malaysian court document formats and conventions',
    icon: 'file',
    static: true,
  },
] as const

function AboutSection({
  lang,
  t,
  openFile,
  classifiedCount,
}: {
  lang: string
  t: (typeof LABELS)['zh']
  openFile: (id: string) => void
  classifiedCount: number
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const [sectionStats, setSectionStats] = useState<Record<string, { count: number; lastUpdated: string }>>({})
  const [sectionFiles, setSectionFiles] = useState<Record<string, RecentFile[]>>({})

  // Load statutes stats
  useEffect(() => {
    fetch('/knowledge/categories?shared=true')
      .then((r) => r.json())
      .then((data) => {
        if (!data.categories) return
        const cats = data.categories as { key: string; file_count: number; last_updated?: string }[]
        const statCat = cats.find((c) => c.key === 'statutes')
        const statuteCount = statCat?.file_count ?? 0
        const statuteUpdated = statCat?.last_updated ?? ''

        setSectionStats({
          statutes: { count: statuteCount, lastUpdated: statuteUpdated },
          subsidiary: { count: statuteCount, lastUpdated: statuteUpdated },
        })
      })
      .catch(() => {})
  }, [])

  const handleExpand = (key: string) => {
    if (expanded === key) {
      setExpanded(null)
      return
    }
    setExpanded(key)
    if (!sectionFiles[key]) {
      fetch('/knowledge/categories/statutes/files?limit=5&shared=true')
        .then((r) => r.json())
        .then((data) => {
          const files = data.files ?? []
          setSectionFiles((prev) => ({ ...prev, [key]: files }))
        })
        .catch(() => {})
    }
  }

  return (
    <div className="border-t border-brand-200 pt-6">
      <h3 className="text-base font-semibold text-brand-800 mb-4">
        {t.aboutTitle}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ABOUT_SECTIONS.map((section) => {
          const title = lang === 'zh' ? section.title_zh : section.title_en
          const desc = lang === 'zh' ? section.desc_zh : section.desc_en
          const Icon = FileText
          const stats = sectionStats[section.key]
          const isExpanded = expanded === section.key
          const files = sectionFiles[section.key] ?? []
          const isStatic = 'static' in section && section.static

          return (
            <div key={section.key} className="flex flex-col">
              <div
                className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                  isStatic ? '' : 'cursor-pointer hover:bg-brand-50'
                } ${isExpanded ? 'bg-blue-50/50 border border-blue-200' : ''}`}
                onClick={() => !isStatic && handleExpand(section.key)}
              >
                <div className="w-9 h-9 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-brand-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-brand-800">
                    {title}
                  </div>
                  <p className="text-xs text-brand-400 mt-0.5 leading-relaxed">
                    {desc}
                  </p>
                  {stats && !isStatic && (
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-brand-500">
                      <span className="font-medium">{stats.count} {lang === 'zh' ? '篇文件' : 'files'}</span>
                      {section.key === 'statutes' && (
                        <span>
                          {lang === 'zh' ? `已归入主题 ${classifiedCount} 篇` : `${classifiedCount} assigned to topics`}
                        </span>
                      )}
                      {stats.lastUpdated && (
                        <span>{lang === 'zh' ? '更新于' : 'Updated'} {stats.lastUpdated.slice(0, 10)}</span>
                      )}
                    </div>
                  )}
                </div>
                {!isStatic && (
                  <ChevronRight className={`w-4 h-4 text-brand-300 flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                )}
              </div>

              {/* 展开预览 */}
              {isExpanded && (
                <div className="mt-2 ml-12 space-y-1.5">
                  {files.length === 0 ? (
                    <div className="text-xs text-brand-400 py-2">
                      {lang === 'zh' ? '加载中...' : 'Loading...'}
                    </div>
                  ) : (
                    files.map((file) => (
                      <div
                        key={file.precedent_id}
                        onClick={() => openFile(file.precedent_id)}
                        className="flex items-center gap-2 py-1.5 px-2 -mx-2 rounded-lg hover:bg-white cursor-pointer transition-colors group/f"
                      >
                        <FileText className="w-3.5 h-3.5 text-brand-300 flex-shrink-0" />
                        <span className="text-xs text-brand-600 truncate group-hover/f:text-blue-600 transition-colors">
                          {file.case_name}
                        </span>
                        {file.decision_date && (
                          <span className="text-[10px] text-brand-300 flex-shrink-0 ml-auto">
                            {file.decision_date}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
